import type { SvgIconComponent } from '@mui/icons-material';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/authContext';
import { BANNER_OFFSET, HEIGHT_BELOW_BARS } from '../../core/bannerOffset';
import { leaveGuarded } from '../../core/leaveGuard';
import { FEEDBACK_SURVEY_URL, feedbackSurveyUrl } from '../../core/surveyLink';
import { logEvent } from '../../utils/eventLog';
import { FlashlightLogo } from './FlashlightLogo';
import { APP_LINK_STATE, navPathForId, pathToActiveId } from './navRoutes';
import { sectionsForRole } from './sidebarSections';

type NavItem = {
  id: string;
  label: string;
  Icon: SvgIconComponent;
};

type Section = {
  label: string;
  items: NavItem[];
};

type SidebarProps = {
  sections: Section[];
  activeId?: string;
};

const SIDEBAR_BG = '#151547';
const SIDEBAR_WIDTH = 256;
const ACTIVE_BG = '#2A2A8B';
const SECTION_LABEL_COLOR = '#8C8CB8';
const ITEM_COLOR = '#E4E4F0';
const HAIRLINE = '1px solid rgba(255,255,255,0.08)';
const HEADER_RULE = '1px solid rgba(255,255,255,0.3)';
const EDGE_HAIRLINE = '1px solid rgba(255,255,255,0.06)';
const HOVER_BG = 'rgba(255,255,255,0.06)';
const FOCUS_RING = '2px solid rgba(255,255,255,0.4)';

export function Sidebar({ sections, activeId: activeIdOverride }: SidebarProps) {
  const { user, district, signOut, isSuperAdmin, isDistrictAdmin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeId = activeIdOverride ?? pathToActiveId(pathname);
  const visibleSections = sectionsForRole(sections, { isSuperAdmin, isDistrictAdmin });
  const go = (path: string) => leaveGuarded(() => navigate(path, { state: APP_LINK_STATE }));
  const leaveToSignOut = () => leaveGuarded(() => { void signOut(); });
  return (
    <Box
      component="nav"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        bgcolor: SIDEBAR_BG,
        color: ITEM_COLOR,
        position: 'sticky',
        top: BANNER_OFFSET,
        alignSelf: 'flex-start',
        height: HEIGHT_BELOW_BARS,
        display: 'flex',
        flexDirection: 'column',
        borderRight: EDGE_HAIRLINE,
      }}
    >
      <Box
        sx={{
          minHeight: 80,
          flexShrink: 0,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          borderBottom: HEADER_RULE,
        }}
      >
        <FlashlightLogo height={32} />
        {(isSuperAdmin || isDistrictAdmin) && district?.name && (
          <Typography
            sx={{ mt: 0.5, fontSize: 12, fontWeight: 700, color: '#FFFFFF', pl: 4.5 }}
          >
            {district.name}
          </Typography>
        )}
      </Box>

      <Stack sx={{ py: 1.5, flex: 1, minHeight: 0, overflowY: 'auto' }} spacing={0.5}>
        {visibleSections.map((section) => (
          <Box key={section.label} sx={{ pb: 0.5 }}>
            <Typography
              sx={{
                px: 2.5,
                pt: 1.5,
                pb: 0.75,
                fontSize: 11,
                fontWeight: 600,
                color: SECTION_LABEL_COLOR,
                letterSpacing: 0.2,
              }}
            >
              {section.label}
            </Typography>
            {section.items.map(({ id, label, Icon }) => {
              const active = id === activeId;
              const path = navPathForId(id);
              const navigable = path !== undefined;
              return (
              <Box
                key={id}
                role={navigable ? 'button' : undefined}
                tabIndex={navigable ? 0 : undefined}
                aria-current={active ? 'page' : undefined}
                onClick={navigable ? () => go(path) : undefined}
                onKeyDown={
                  navigable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          go(path);
                        }
                      }
                    : undefined
                }
                sx={{
                  mx: 1,
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderRadius: 1.5,
                  cursor: navigable ? 'pointer' : 'default',
                  bgcolor: active ? ACTIVE_BG : 'transparent',
                  color: active ? '#FFFFFF' : ITEM_COLOR,
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: active ? ACTIVE_BG : HOVER_BG },
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{label}</Typography>
              </Box>
              );
            })}
          </Box>
        ))}
      </Stack>

      <Box sx={{ flexShrink: 0, px: 1, pb: 1.5 }}>
        <Box
          component="a"
          href={feedbackSurveyUrl(user?.id)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            void logEvent('feedback.link_clicked.v1', {
              source: 'sidebar',
              destination: FEEDBACK_SURVEY_URL,
            }).catch(() => {});
          }}
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: 1.5,
            cursor: 'pointer',
            color: ITEM_COLOR,
            textDecoration: 'none',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: HOVER_BG },
            '&:focus-visible': { outline: FOCUS_RING },
          }}
        >
          <FeedbackOutlinedIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Send feedback</Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 'auto',
          px: 1,
          py: 1.5,
          borderTop: HAIRLINE,
        }}
      >
        {user?.first_name && (
          <Typography
            sx={{ px: 1.5, pb: 0.5, fontSize: 12, color: SECTION_LABEL_COLOR }}
          >
            Signed in as {user.first_name}
          </Typography>
        )}
        <Box
          role="button"
          tabIndex={0}
          onClick={leaveToSignOut}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              leaveToSignOut();
            }
          }}
          sx={{
            mx: 0,
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: 1.5,
            cursor: 'pointer',
            color: ITEM_COLOR,
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: HOVER_BG },
            '&:focus-visible': { outline: FOCUS_RING },
          }}
        >
          <LogoutOutlinedIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Sign out</Typography>
        </Box>
      </Box>
    </Box>
  );
}
